/* ────────────────────────────────────────────────────────────────
   src/views/admin/users/leave/index.jsx
   (branch-aware headers; list/delete respect selected branch)
   ──────────────────────────────────────────────────────────────── */
import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useSelector } from 'react-redux';
import Swal from 'sweetalert2';

import Breadcrumb from '@/components/common/Breadcrumb';
import CustomDropdown from '@/components/common/CustomDropdown';
import Card from '@/components/ui/cards/Card';
import CardHeader from '@/components/ui/cards/CardHeader';
import PerPageSelection from '@/components/ui/tables/PerPageSelection';
import { API_BASE_URL } from '@/constants/ApiConstant';
import { cn } from '@/lib/utils';

import DataTableFilter from './component/DataTableFilter';
import DataTable from './component/DataTable';
import AddLeaveDrawer from './component/AddLeaveDrawer';

const API_BASE = `${API_BASE_URL}/leave-request`;

/** Centralised JWT lookup (Redux → localStorage fallbacks) */
const useJwt = () => {
  const reduxTok = useSelector((s) => s.Auth?.token);
  return (
    reduxTok ||
    localStorage.getItem('jwt_token') ||
    localStorage.getItem('token') ||
    localStorage.getItem('adminToken') ||
    ''
  );
};

/** Build headers with Authorization + tenant (company/branch) */
const buildAuthHeaders = (user, jwt) => {
  const h = {};
  if (jwt) h.Authorization = `Bearer ${jwt}`;

  const companyId =
    localStorage.getItem('X-Company-Id') ||
    (typeof user?.company_id === 'object' ? user?.company_id?._id : user?.company_id) ||
    '';

  const branchId =
    localStorage.getItem('X-Branch-Id') ||
    (typeof user?.default_branch_id === 'object' ? user?.default_branch_id?._id : user?.default_branch_id) ||
    '';

  if (companyId) h['X-Company-Id'] = companyId;
  if (branchId) h['X-Branch-Id'] = branchId;

  return h;
};

export default function LeaveRequestsPage({ pageTitle = 'Leave Requests' }) {
  const jwtToken = useJwt();
  const { userData: user } = useSelector((s) => s.Auth);

  const authHdr = useMemo(() => buildAuthHeaders(user, jwtToken), [user, jwtToken]);

  const isHR = useMemo(
    () => !!(user?.isSuperAdmin || user?.permissions?.includes('view_leave_all')),
    [user]
  );
  const canRequest = useMemo(
    () => !!(user?.isSuperAdmin || user?.permissions?.includes('request_leave')),
    [user]
  );
  const canManage = useMemo(
    () => !!(user?.isSuperAdmin || user?.permissions?.includes('manage_leave')),
    [user]
  );
  const canDeleteSelf = useMemo(
    () => !!user?.permissions?.includes('delete_leave'),
    [user]
  );

  const [dataSource, setDataSource] = useState([]);
  const [totalRecord, setTotalRecord] = useState(0);
  const [activeDrawer, setActiveDrawer] = useState(false);
  const [editDataId, setEditDataId] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isActive, setIsActive] = useState(false);
  const filterDivRef = useRef(null);

  const [dataFilter, setDataFilter] = useState({
    page: 1,
    limit: 10,
    extra_filter: {}, // { status, employee, date range } handled by DataTableFilter
  });

  const tableColumn = useMemo(() => ([
    ...(isHR ? [{ name: 'Employee', isIncludePrint: true }] : []),
    { name: 'Leave Type', isIncludePrint: true },
    { name: 'Start Date', isIncludePrint: true },
    { name: 'End Date', isIncludePrint: true },
    { name: 'Reason', isIncludePrint: true },
    { name: 'Status', isIncludePrint: true },
    { name: 'Action', isIncludePrint: false },
  ]), [isHR]);

  useEffect(() => {
    getTableData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dataFilter.page, dataFilter.limit, jwtToken, authHdr]);

  async function getTableData() {
    if (!jwtToken) return;
    try {
      setIsLoading(true);

      const params = new URLSearchParams();
      params.set('page', String(dataFilter.page));
      params.set('limit', String(dataFilter.limit));

      // Optional: forward filter values (status, employee)
      if (dataFilter?.extra_filter?.status !== undefined && dataFilter?.extra_filter?.status !== '') {
        params.set('status', String(dataFilter.extra_filter.status));
      }
      if (dataFilter?.extra_filter?.employee) {
        params.set('employee', String(dataFilter.extra_filter.employee));
      }

      const res = await fetch(`${API_BASE}?${params.toString()}`, { headers: authHdr });
      // Handle unauthorized quickly
      if (res.status === 401) {
        Swal.fire('Session expired', 'Please sign in again.', 'warning');
        setDataSource([]);
        setTotalRecord(0);
        return;
      }

      const json = await res.json();
      if (json?.success) {
        const rows = Array.isArray(json.data) ? json.data : [];
        setDataSource(rows);
        setTotalRecord(rows.length);
      } else {
        setDataSource([]);
        setTotalRecord(0);
      }
    } catch (err) {
      console.error(err);
      setDataSource([]);
      setTotalRecord(0);
    } finally {
      setIsLoading(false);
    }
  }

  async function deleteRecord(id, { ownedByUser = false } = {}) {
    // Permission gate: allow if HR/manager OR (self + has delete_leave)
    if (!(canManage || (ownedByUser && canDeleteSelf))) {
      Swal.fire('Forbidden', 'You do not have permission to delete this leave.', 'error');
      return;
    }

    const { isConfirmed } = await Swal.fire({
      title: 'Delete this leave request?',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: 'Yes, delete',
    });
    if (!isConfirmed) return;

    try {
      setIsLoading(true);
      const res = await fetch(`${API_BASE}/${id}`, {
        method: 'DELETE',
        headers: authHdr,
      });

      const json = await res.json();
      if (json?.success) {
        Swal.fire('Deleted!', '', 'success');
        getTableData();
      } else {
        Swal.fire('Error', json?.message || 'Unable to delete leave', 'error');
      }
    } catch (err) {
      console.error(err);
      Swal.fire('Error', 'Network or server error', 'error');
    } finally {
      setIsLoading(false);
    }
  }

  const handleDrawerOpen = (id = '') => {
    setEditDataId(id);
    setActiveDrawer(true);
    document.body.style.overflowY = 'hidden';
  };

  const onChangeFilter = (name, value) =>
    setDataFilter((prev) => ({ ...prev, [name]: value }));

  const handleFilterToggle = () => {
    if (!filterDivRef.current) return;
    setIsActive((prev) => !prev);
    filterDivRef.current.style.height = !isActive ? '100%' : '0px';
  };

  if (!jwtToken) {
    return (
      <p style={{ padding: 20, textAlign: 'center' }}>
        You’re not signed in. Please{' '}
        <a href="/sign-in" className="underline text-blue-600">sign in</a>{' '}
        to view leave requests.
      </p>
    );
  }

  return (
    <>
      <Breadcrumb pageTitle={pageTitle} />

      <Card>
        <CardHeader>
          <h3 className="db-card-title">{pageTitle}</h3>
          <div className="db-card-filter">
            <PerPageSelection
              onChangeParPage={(n, v) => onChangeFilter(n, v)}
              limit={dataFilter.limit}
            />

            <button
              className="db-card-filter-btn table-filter-btn"
              onClick={handleFilterToggle}
              type="button"
            >
              <i className="lab lab-filter lab-font-size-14" />
              <span>Filter</span>
            </button>

            <CustomDropdown
              buttonText="Export"
              buttonIcon="lab-file-export"
              dropdownList={[]}
            />

            {canRequest && (
              <button
                type="button"
                onClick={() => handleDrawerOpen('')}
                className="db-btn h-[37px] text-white bg-primary"
              >
                <i className="lab lab-add-circle-line" /> Add Leave
              </button>
            )}
          </div>
        </CardHeader>

        <div className={cn('table-filter-div', isActive && 'active')} ref={filterDivRef}>
          <DataTableFilter
            onChangeFilter={(key, val) => {
              if (key === 'limit') return onChangeFilter('limit', val);
              if (key === 'page') return onChangeFilter('page', val);
              // everything else into extra_filter
              setDataFilter((prev) => ({
                ...prev,
                extra_filter: { ...(prev.extra_filter || {}), [key]: val },
                page: 1, // reset page on new filter
              }));
            }}
            values={dataFilter}
            isHR={isHR}
          />
        </div>

        <DataTable
          isLoading={isLoading}
          tableColumn={tableColumn}
          dataSource={dataSource}
          totalRecord={totalRecord}
          dataFilter={dataFilter}
          onChangePage={(name, val) => onChangeFilter(name, val)}
          handleDrawerOpen={handleDrawerOpen}
          deleteRecord={deleteRecord}
          refresh={getTableData}
          authHeaders={authHdr}        // for row actions (approve/status)
          isHR={isHR}
          canManage={canManage}
          canDeleteSelf={canDeleteSelf}
          currentUserId={user?._id}
        />

        {canRequest && (
          <AddLeaveDrawer
            titleText={pageTitle}
            getTableData={getTableData}
            activeDrawer={activeDrawer}
            setActiveDrawer={(open) => {
              setActiveDrawer(open);
              if (!open) document.body.style.overflowY = 'auto';
            }}
            editDataId={editDataId}
            authHeaders={authHdr}
          />
        )}
      </Card>
    </>
  );
}
